import type { EarningsRange } from "@pax/api-contract/accounting/v1alpha1";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { field, fieldLabel, Heading, Workspace } from "@/features/evals/components";
import { Note } from "@/features/ledger/components";
import { EarningsRangeTabs, PresetEarnings } from "./PresetEarnings";
import { earningsByDayQuery } from "./queries";

/** Which earnings show: one preset's or all of them, over which range, how far back. */
export interface EarningsSearch {
  /** One preset's earnings instead of everything the reader earned. */
  presetId?: string;
  range?: EarningsRange;
  /** How many ranges back from the newest. */
  back?: number;
}

/** The earnings page: the range and preset pickers over the charts, under a heading. */
export function EarningsScreen({
  search,
  onSearchChange,
  onBack,
}: {
  search: EarningsSearch;
  /** `replace` when the change shouldn't be a step of its own to go back through. */
  onSearchChange: (next: EarningsSearch, replace?: boolean) => void;
  /** The way back, when it opens over the app. */
  onBack?: () => void;
}) {
  return (
    <Workspace back={onBack}>
      <Heading title="Preset earnings" />
      <Earnings search={search} onSearchChange={onSearchChange} />
    </Workspace>
  );
}

/**
 * The creator rewards the reader earned, stacked by preset. With a preset picked, what that preset
 * paid out: split by recipient for its team, the reader's own share for anyone else it paid.
 * The earnings page shows it under a heading, and your own profile in its Earnings tab.
 */
export function Earnings({
  search,
  onSearchChange,
}: {
  search: EarningsSearch;
  /** `replace` when the change shouldn't be a step of its own to go back through. */
  onSearchChange: (next: EarningsSearch, replace?: boolean) => void;
}) {
  const { presetId, range = "30d", back = 0 } = search;
  const [parts, setParts] = useState<string[]>();
  const mine = useQuery(earningsByDayQuery({}, range, back));
  const presets = (mine.data?.parts ?? []).filter(part => part.presetId);
  const picked = presets.find(part => part.presetId === presetId);
  const nothing = <Note>Nothing to show for this preset.</Note>;
  const step = (next: number) =>
    onSearchChange({
      presetId,
      range: range === "30d" ? undefined : range,
      back: next || undefined,
    });
  const pick = (next: EarningsSearch) => {
    setParts(undefined);
    onSearchChange({ ...next, range, back: back || undefined });
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <EarningsRangeTabs
          range={range}
          onChange={next =>
            onSearchChange({ presetId, range: next === "30d" ? undefined : next }, true)
          }
        />
        <label className="flex items-center gap-2">
          <span className={fieldLabel}>Preset</span>
          <select
            value={presetId ?? ""}
            onChange={event => pick({ presetId: event.target.value || undefined })}
            className={`w-64 max-w-full ${field}`}
          >
            <option value="">All my presets</option>
            {presetId && !picked && <option value={presetId}>This preset</option>}
            {presets.map(part => (
              <option key={part.presetId} value={part.presetId ?? ""}>
                {part.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {presetId ? (
        <PresetEarnings
          key={presetId}
          filter={{ presetId }}
          range={range}
          back={back}
          onBackChange={step}
          parts={parts ?? []}
          onPartsChange={setParts}
          notFound={nothing}
        />
      ) : (
        <PresetEarnings
          filter={{}}
          range={range}
          back={back}
          onBackChange={step}
          parts={parts ?? []}
          onPartsChange={setParts}
        />
      )}
    </div>
  );
}
